<template>
    <div v-if="product" id="product-panel">

        <div class="product-panel-header">
            <img class="aliaxis-logo" :src="getMedia('logos/aliaxis_logo-white.svg')" alt="Aliaxis Logo">
            
            <button class="closeBtn-light" @click="closePanel()">
                <img class="closeBtn-img" :src="getMedia('icons/ic_cross.svg')" alt="Close">
            </button>
        </div>

        <div class="product-panel-body">
            <div class="product-media">
                <div class="media-container">
                    <img v-if='product.data.img && product.data.img != ""' id="info-panel-img" :src='product.data.img' alt="">
                </div>
                <div class="media-container" v-if='product.data.video && product.data.video != ""'>
                    <iframe v-if='product.data.video.includes("youtube")' :src='product.data.video.replace("watch?v=","embed/")' frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
                    <video v-else :src='product.data.video' controls preload="auto"></video>
                </div>
            </div>

            <div class="product-profile">
                <img class="product-firm-logo" :src="product.data.logo" alt="">
                <p v-if="product.data.group != '' || product.data.code != ''" class="product-label">
                    <span v-if="product.data.group != ''">{{product.data.group}} </span>
                    <span v-if="product.data.code != ''">{{product.data.code}} </span>
                </p>
                <h1 v-if="product.data.title != ''" class="product-name">{{product.data.title}}</h1>
                
                <div v-show='productDescription' v-html="productDescription" class="product-description"></div>
                
                <div class="product-profile-links">
                    <button v-if='product.data.button && product.data.button.url != ""' @click="openLink(product.data.button.url)">{{ product.data.button.label }}</button>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getMedia } from "@/util";
import { storeToRefs } from 'pinia';
import { useStore } from '@/store';

const store = useStore();
const { selectedProduct, activeLanguage } = storeToRefs(store);

const product = computed(() => {        
    return selectedProduct.value ? {
        _ID: selectedProduct.value.DO_NOT_CHANGE_ID,
        _segment: selectedProduct.value.DO_NOT_CHANGE_segment,
        data: selectedProduct.value.data.find((data) => data.lang == activeLanguage.value)!
    } : null;
});

const productDescription = computed(() => {
    if (product.value && product.value.data.description)
        return product.value.data.description.trim();
    return "";
});

const closePanel = () => {
    store.setInfoPanelOpen(false);
};

const openLink = (url: string) => {
    window.open(url, '_blank');
};
</script>

<style lang="scss">
#product-panel{
    --panel-width: 30rem;

    position: fixed;
    top: 0;
    left:0;
    width: var(--panel-width);
    height: 100%;

    background: white;
    pointer-events: all;

    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.product-panel-header{
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 4rem;
    background: linear-gradient(to right, var(--color-lightblue),var(--color-blue));
    box-sizing: border-box;

    .aliaxis-logo{
        height: 1.5rem;
    }

    .closeBtn-light{
        position:absolute;
        top: 50%;
        right: 1rem;
        transform: translateY(-50%);
        background: transparent;

        img{
            height: 1.5rem;
        }
    }

}

.product-panel-body{
    position: relative;
    flex-grow: 1;
    isolation: isolate;
    overflow: auto;

    .product-media{
        width: 100%;
        height: 66%;
        background: var(--color-lightgray);
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        z-index: 1;

        .media-container{
            position: relative;
            min-width: var(--panel-width);
            height: 100%;
        }

        img, video, iframe{
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .product-profile{
        
        min-height: 34%;
        z-index: 2;
        background: white;
        box-sizing: border-box;
        border-top: 1px solid rgba(0,0,0,0.1);

        padding: 1rem 1.5rem 2rem 1.5rem;

        &>*{
            margin: 0;
        }

        .product-firm-logo{
            height: 2rem;
            margin-bottom: 0.5rem;
        }

        .product-name{
            font-size: 1.75rem;
            margin-bottom: 0.25rem;
            line-height: 2.25rem;
            color: var(--color-lightblue);
        }

        .product-label{
            line-height: 1.25rem;
            font-size: 1rem;
            opacity: 0.5;

            span:not(:last-child)::after {
                content: " | ";
            }
        }

        .product-description{
            font-size: 1rem;
            line-height: 1.5rem;
        }

        .product-profile-links{
            width: 100%;
            margin-top: 1rem;

            button{
                width: 100%;
                height: 3rem;
                background: var(--color-blue);
                color: white;
                border: none;
                border-radius: 0.25rem;
                cursor: pointer;
                font-size: 1rem;

                &:hover{
                    background: color-mix(in oklab, var(--color-blue) 90%, black);
                }
            }
        }

    }
}

    
@media (max-width: 900px){
    #product-panel{
        --panel-width: 100vw;
    }
}
</style>